// Countertops/Cabinets manual estimate — persistence payload <-> frontend model mapping.
//
// The estimate is a flat list of MATERIAL and LABOR line items in the OrderConfirmation shape
// (pulled in via the shared PricingContract "Add Scope" modal). These pure adapters map between
// that in-memory model ({ description, materialList, laborList }) and the save/load API shape,
// reusing existing entity ids as persistence keys (contactInfoId, storeId, unitTypeId/variantId).
// The `|| 0` id convention applies: estimateId 0 => create, > 0 => update.

import { recalcLinePrice, estimateItemsTotal } from "./manualEstimateItems";

const orZero = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const str = (v) => (v === null || v === undefined ? "" : String(v));

// Line items are already the OrderConfirmation shape; persist them intact (with linePrice kept
// consistent as qty x unit price) so a reload restores every entered/derived field.
const serializeItems = (list) =>
  (Array.isArray(list) ? list : []).map((item) => recalcLinePrice({ ...item }));

// Build the request payload sent to SaveCountertopCabinetEstimate.
export function buildEstimatePayload(estimate = {}, identifiers = {}) {
  const { description = "", materialList = [], laborList = [] } = estimate;
  const material = serializeItems(materialList);
  const labor = serializeItems(laborList);

  return {
    estimateId: orZero(identifiers.estimateId), // 0 => create, >0 => update
    contactInfoId: orZero(identifiers.contactInfoId),
    storeId: orZero(identifiers.storeId),
    unitTypeId: orZero(identifiers.unitTypeId),
    variantId: orZero(identifiers.variantId),
    isCountertopCabinet: true,
    description: str(description).trim(),
    total: estimateItemsTotal(material, labor),
    material,
    labor,
  };
}

// Rehydrate the frontend model from a saved estimate response.
// Returns { estimateId, description, materialList, laborList }.
export function hydrateEstimate(saved) {
  const source = saved?.result ? saved.result : saved;
  if (!source) return { estimateId: 0, description: "", materialList: [], laborList: [] };

  return {
    estimateId: orZero(source.estimateId),
    description: str(source.description),
    materialList: (Array.isArray(source.material) ? source.material : []).map((i) => ({ ...i })),
    laborList: (Array.isArray(source.labor) ? source.labor : []).map((i) => ({ ...i })),
  };
}
